// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import SystemBus from 'core/entities/SystemBus';
import AudioContext from 'core/sound/AudioContext';
import SpeechManager from './SpeechManager';
import ObjectUtils from 'core/util/ObjectUtils';
import Audio from "core/sound/Audio";
import Asset from 'core/api/common/Asset';

const warnConfigVolume = ObjectUtils.warnOnce("Changing volume via config.volume for Speech Objects is deprecated. Use SpeechComponent.setVolume() instead.");

/**
 * A representation of speech in the engine.
 * @param {SpeechManager} speechManager
 */
class Speech extends Audio{
  constructor(speechManager) {
    super();
    //SpeechManager controls all network Polly calls and maintain a cache of them
    this._speechManager = speechManager ? speechManager : SpeechManager.getInstance();

    // Public fields. Set from ui configuration.
    this.body = null;
    this.id = null;
    this.name = null;
    this.type = null;
    this.voiceId = null;
    this.voiceEngine = null;
    this.volume = 1.0;

    // Private fields
    this._buffer = null;
    this._speechMarks = null;

    // Gesture/SSML event offsets (in milliseconds) to make sure animations start slightly before
    // the corresponding point in the audio.
    // TODO: Make these configurable in the ui
    // SIM: https://sim.amazon.com/issues/SUMERIAN-286
    this._sentenceOffset = 0;
    this._ssmlOffset = -500;
    this._visemeOffset = -35;
    this._wordOffset = 0;

    this._currentSentence = '';
    this._currentSSML = '';
    this._currentViseme = '';
    this._currentWord = '';

    this._currentSentenceMarkIndex = 0;
    this._currentSSMLMarkIndex = 0;
    this._currentVisemeMarkIndex = 0;
    this._currentWordMarkIndex = 0;

    //WebAudio Related Fields
    this._audioContext = AudioContext.getContext();
    /**
     * When, in ms, in the AudioContext buffer the sound was paused.
     * @type {number}
     * @private
     */
    this._pausePos = 0;
    this._endPromise = null;
    this._endPromiseResolve = null;
    this._paused = false;
    this._outNode = this._audioContext.createGain();
  }

  /**
   * Updates the configuration of the Speech object.
   *
   * @typedef {Object} config
   * @property {Entity} config.entity The entity this speech is attached to
   * @property {string} config.id The id of the speech object
   * @property {string} config.name The name of the speech object
   * @property {string} config.body The text content of the speech object.
   * @property {string} config.type The type of content contained in Speech.body (text or ssml).
   * @property {number} config.volume The volume used for playback (0 - 1.0)
   * @property {string} config.voice The voice id used by polly to synthesize the speech.
   */
  updateConfig(config = {}) {
    if (config.entity) {
      this.entity = config.entity;
      // SystemBus events for interacting with other systems.
      this.events = Object.assign({}, {
        // Local to entity SystemBus events to emit when speech starts/stops (for the HostComponent)
        entityStartSpeech: SystemBus.makeChannel(`${this.entity.id + Speech.START_SPEECH_CHANNEL_SUFFIX}`),
        entityStopSpeech: SystemBus.makeChannel(`${this.entity.id + Speech.STOP_SPEECH_CHANNEL_SUFFIX}`),
        entitySentence: SystemBus.makeChannel(`${this.entity.id}.sentenceEvent`),
        entitySSML: SystemBus.makeChannel(`${this.entity.id}.ssmlEvent`),
        entityViseme: SystemBus.makeChannel(`${this.entity.id}.visemeEvent`),
        entityWord: SystemBus.makeChannel(`${this.entity.id}.wordEvent`),
      });
    }

    if (config.id) {
      this.id = config.id;
    }

    if (config.name) {
      this.name = config.name;
    }

    if (config.body !== undefined) {
      if (config.body !== this.body) {
        this._audioDirty = true;
      }
      this.body = config.body;
    }

    if (config.type) {
      this.type = config.type;
    }

    if (config.volume !== undefined) {
      warnConfigVolume(); 
      this.volume = config.volume;
      this._outNode.gain.value = this.volume;
    }

    if (config.voice) {
      if (config.voice !== this.voiceId) {
        this._audioDirty = true;
      }
      this.voiceId = config.voice;
    }

    if (config.voiceEngine) {
      if (config.voiceEngine !== this.voiceEngine) {
        this._audioDirty = true;
      }
      this.voiceEngine = config.voiceEngine;
    }

    if(this._audioDirty && this.body){
      this._speechManager.postConfig({
        voiceId: this.voiceId,
        voiceEngine: this.voiceEngine,
        body: this.body,
      });
      this._audioDirty = false;
    }
  }

  /**
   * Connect output of speech to audionode
   * @param {Array<AudioNode> | AudioNode} nodes
   */
  connectTo(nodes) {
    this._outNode.disconnect();
    if (!nodes) {
      return;
    }
    if (!(nodes instanceof Array)) {
      nodes = [nodes];
    }
    for (let i = 0; i < nodes.length; i++) {
      this._outNode.connect(nodes[i]);
    }
  };

  /**
   * Gets the play status of the speech
   * @returns {boolean} is the speech playing
   */
  isPlaying() {
    return !!this._currentSource;
  }

  /**
   * Gets the pause status of the speech
   * @returns {boolean} is the speech paused
   */
  isPaused() {
    return this._paused;
  }


  /**
   * Plays the audio for the speech.
   * It will try to get audioBuffer and speechMarks from {@link SpeechManager#getAudioAndSpeechMark}
   * @returns {Promise} Resolves when speech has completed or when it's stopped.
   */
  play() {
    if (this._currentSource) {
      return this._endPromise;
    }

    this._outNode.gain.value = this.volume;

    return this._speechManager.getAudioAndSpeechMark({body: this.body, voiceId: this.voiceId, voiceEngine: this.voiceEngine})
      .then((result) => {
        this._buffer = result.audioBuffer;
        this._speechMarks = result.speechMark;

        this._currentSource = this._audioContext.createBufferSource();

        this._endPromise = new Promise(resolve => this._endPromiseResolve = resolve);

        this._paused = false;
        this._currentSource.onended = () => {
          if(!this._stopped){
            this.stop();
          }
        };

        this._currentSource.buffer = this._buffer;
        this._currentSource.connect(this._outNode);

        this._startTime = this.currentTime;

        this.autoplayableStart(0, this._pausePos);

        this.events.entityStartSpeech.emit();
        this._emitStartSpeechEvent();

        return this._endPromise;
      })
      .catch((err) => {
        if (err.name === 'InvalidSsmlException') {
          console.error(`[Sumerian] Invalid SSML in ${this.id}, please ensure you are using supported SSML tags: https://docs.aws.amazon.com/polly/latest/dg/supportedtags.html`);
        }
        return Promise.reject(err);
      });
  }

  /**
   * Pause the speech if it's playing and record the pause position
   */
  pause() {
    if(this._currentSource){
      this._paused = true;
      this._pausePos = this.currentTime - this._startTime;
      this._stop(false);
    }

    // return a resolved promise for StopSpeechAction
    return Promise.resolve();
  }

  /**
   * Resume the speech if paused
   */
  resume() {
    if(this._paused) {
      this.play();
    }
  }

  /**
   * External call to the stop
   */
  stop() {
    this._paused = false;
    this._pausePos = 0;
    if(this._endPromiseResolve){
      this._endPromiseResolve();
    }
    if(this._currentSource){
      this._stop(true);
    }

    // return a resolved promise for StopSpeechAction
    return Promise.resolve();
  }

  /**
   * Internal stop function which stops the _currentSource and reset the speech if needed
   * @param {boolean} resetSpeech
   * @private
   */
  _stop(resetSpeech) {
    if(!this._suspended) {
      this._currentSource.stop();
    }
    this._stopped = true;
    this._currentSource = null;
    this._stopAudio(resetSpeech);
  }

  /**
   * Resets the internal state of the instance if resetSpeech flag is true.
   * @param {boolean} resetSpeech
   * @private
   */
  _stopAudio(resetSpeech = true) {
    if (resetSpeech) {
      this._currentSentenceMarkIndex = 0;
      this._currentSSMLMarkIndex = 0;
      this._currentVisemeMarkIndex = 0;
      this._currentWordMarkIndex = 0;
      this._currentSentence = '';
      this._currentSSML = '';
      this._currentViseme = '';
      this._currentWord = '';
    }

    this._emitStopSpeechEvent();
    this.events.entityStopSpeech.emit();
  }

  /**
   * Processes speech marks if any to emit the proper system bus events for gesture control.
   * Called on each render frame.
   */
  process() {
    if (this._speechMarks) {
      if (!this._currentSource) {
        if (this._currentViseme !== 'sil') {
          this._switchViseme('sil');
        }
      }
      else {
        const sentence = this._speechMarks.sentence[this._currentSentenceMarkIndex];
        const ssml = this._speechMarks.ssml[this._currentSSMLMarkIndex];
        const viseme = this._speechMarks.viseme[this._currentVisemeMarkIndex];
        const word = this._speechMarks.word[this._currentWordMarkIndex];

        if (this._currentVisemeMarkIndex >= this._speechMarks.viseme.length) {
          this._switchViseme('sil');
          this._emitStopSpeechEvent();
        }
        else {
          const elapsedTime = this.currentTime - this._startTime;
          const sentenceTime = elapsedTime - this._sentenceOffset;
          const ssmlTime = elapsedTime - this._ssmlOffset;
          const visemeTime = elapsedTime - this._visemeOffset;
          const wordTime = elapsedTime - this._wordOffset;
          if (
            this._currentSentenceMarkIndex <
            this._speechMarks.sentence.length &&
            sentenceTime > sentence.time
          ) {
            this._switchSentence(sentence.value.trim());
            this._currentSentenceMarkIndex++;
          }
          if (
            this._currentSSMLMarkIndex < this._speechMarks.ssml.length &&
            ssmlTime > ssml.time
          ) {
            this._switchSSML(ssml.value.trim());
            this._currentSSMLMarkIndex++;
          }
          if (visemeTime > viseme.time) {
            this._switchViseme(viseme.value.trim());
            this._currentVisemeMarkIndex++;
          }
          if (
            this._currentWordMarkIndex < this._speechMarks.word.length &&
            wordTime > word.time
          ) {
            this._switchWord(word.value.trim());
            this._currentWordMarkIndex++;
          }
        }
      }
    }
  }

  /**
   * Emits the proper system bus events for speech mark data.
   *
   * @param {string} type
   * @param {string} data
   * @private
   */
  _emitData(type, data) {
    switch (type) {
      case 'sentence':
        this.events.entitySentence.emit(data);
        break;
      case 'ssml':
        this.events.entitySSML.emit(data);
        break;
      case 'viseme':
        this.events.entityViseme.emit(data);
        break;
      case 'word':
        this.events.entityWord.emit(data);
        break;
      default:
        break;
    }
  }

  /**
   * Emits the global start speech event.
   *
   * @private
   */
  _emitStartSpeechEvent() {
    Speech.globalEvents.startSpeech.emit();
  }

  /**
   * Emits the global stop speech event.
   *
   * @private
   */
  _emitStopSpeechEvent() {
    Speech.globalEvents.stopSpeech.emit();
  }

  /**
   * Updates the current sentence mark.
   *
   * @param {string} sentence
   * @private
   */
  _switchSentence(sentence = '') {
    this._currentSentence = sentence;
    this._emitData('sentence', this._currentSentence);
  }

  /**
   * Updates the current ssml mark.
   *
   * @param {string} ssml
   * @private
   */
  _switchSSML(ssml = '') {
    this._currentSSML = ssml;
    this._emitData('ssml', this._currentSSML);
  }

  /**
   * Updates the current viseme mark.
   *
   * @param {string} viseme
   * @private
   */
  _switchViseme(viseme = '') {
    this._currentViseme = viseme;
    this._emitData('viseme', this._currentViseme);
  }

  /**
   * Updates the current word mark.
   *
   * @param {string} word
   * @private
   */
  _switchWord(word = '') {
    this._currentWord = word;
    this._emitData('word', this._currentWord);
  }

  /**
   * Gets the internal api Asset object for a specified public api Speech object.
   *
   * @param {Asset} publicAsset - The public api Speech object.
   *
   * @return {Speech} The internal api Speech object.
   */
  static forPublicAsset(publicAsset) {
    return publicAsset._internalAsset;
  }

  /**
   * Gets the public api Speech object for this internal api Speech object.
   *
   * @return {Asset} The public api Speech object.
   */
  get publicAsset() {
    if(!this._publicAsset) {
      this._publicAsset = new Asset(this);
    }
    return this._publicAsset;
  }
}

/**
 * @deprecated
 * Updates the playback volume for the audio element.
 * This does not override the value for the speech object. It will be reset to the speech object's value
 * when the audio is done playing.
 *
 * @param {number} volume
 * @returns {Promise} resolves with the set volume
 */
Speech.prototype.setVolume = ObjectUtils.warnOnce('Speech.prototype.setVolume() is deprecated. Please use SpeechComponent.prototype.setVolume() instead.',
  function(volume)  {
    this.volume = volume;
    this._outNode.gain.value = this.volume;
    return Promise.resolve(this.volume);
  });

// SystemBus events for interacting with other systems.
Speech.globalEvents = {
  // Global events to request start/stop speech
  startSpeech: SystemBus.makeChannel('startSpeechEvent'),
  stopSpeech: SystemBus.makeChannel('stopSpeechEvent'),
};

Speech.START_SPEECH_CHANNEL_SUFFIX = '.startSpeechEvent';
Speech.STOP_SPEECH_CHANNEL_SUFFIX = '.stopSpeechEvent';

export default Speech;
